/**
 * The three designed empty states for a lead list (PREC-3542, mockup
 * "signalist-empty-state-v1"):
 * - `never`    — the list has never produced any leads in any status.
 * - `filtered` — there are no Found (qualified) leads, but disqualified leads
 *                (Unsure / Not found / Blacklist) are still on the list.
 * - `aged`     — the list has leads, but they were all assigned or moved to
 *                Ignored (e.g. auto-ignored after 30 days unassigned).
 */
export type ListEmptyStateVariant = "never" | "filtered" | "aged";

/**
 * Lean-list lead counts the detection reads (all optional → treated as `0`).
 *
 * IMPORTANT: these are the *unscoped* list counts (lean query with no `assignStatus`) — they drive
 * the list-level `never` / `aged` decision, which must be correct on every tab. The tab-scoped lean
 * query narrows the inclusion counts (Unsure / Not found / Blacklist) to the active tab's assign
 * status, which would misclassify the list-level state. The tab-scoped disqualified count that
 * drives the `filtered` decision is passed separately as
 * {@link GetListEmptyStateParams.scopedDisqualifiedCount}.
 */
export interface ListEmptyStateCounts {
	draftCount?: number;
	savedForLaterCount?: number;
	assignedCount?: number;
	archivedCount?: number;
	doneCount?: number;
	underReviewCount?: number;
	excludedCount?: number;
	maybeCount?: number;
	blacklistCount?: number;
}

export interface GetListEmptyStateParams extends ListEmptyStateCounts {
	/** A search term, filter-modal filter, or non-default signal/priority filter is active. */
	hasActiveFilters: boolean;
	/**
	 * Disqualified-lead count (Unsure + Not found + Blacklist) scoped to the CURRENT tab's assign
	 * status — i.e. the leads the `filtered` variant's "Review all" action can actually surface (it
	 * only flips the inclusion filter *within* the current tab, it does not change tab). Drives the
	 * `filtered` vs `aged` choice so we never promise "Review all N" for disqualified leads parked in
	 * another tab (e.g. Ignored), which the action could not reveal. Omit it to fall back to the
	 * unscoped disqualified total.
	 */
	scopedDisqualifiedCount?: number;
}

/**
 * Coerce a lean-list count to a number. These fields are typed `number` but the API returns them
 * as strings (MySQL aggregate columns), so summing them with `+` would concatenate. Always run
 * raw counts through this before arithmetic. Invalid/missing values fall back to `0`.
 */
export const toCount = (value?: number): number => Number(value ?? 0) || 0;

/**
 * Decide which designed empty state (if any) to show when a list table renders with zero rows.
 * Returns `null` when the generic "no results" message should be used instead — i.e. when filters
 * are active, or there are Found (qualified) leads and this particular view is just empty for
 * another reason (a different tab / inclusion selection).
 */
export const getListEmptyState = ({
	draftCount,
	savedForLaterCount,
	assignedCount,
	archivedCount,
	doneCount,
	underReviewCount,
	excludedCount,
	maybeCount,
	blacklistCount,
	scopedDisqualifiedCount,
	hasActiveFilters,
}: GetListEmptyStateParams): ListEmptyStateVariant | null => {
	if (hasActiveFilters) {
		return null;
	}

	// The lean-list counts arrive from the API as strings (MySQL aggregate columns), so coerce
	// before any arithmetic — otherwise `+` concatenates (e.g. "6" + "0" + "1" → "601").
	const draft = toCount(draftCount);
	const disqualified = toCount(excludedCount) + toCount(maybeCount) + toCount(blacklistCount);
	const engaged =
		toCount(savedForLaterCount) +
		toCount(assignedCount) +
		toCount(archivedCount) +
		toCount(doneCount) +
		toCount(underReviewCount);

	// `filtered` is the only variant whose action ("Review all") stays on the current tab, so it
	// must be decided on the tab-scoped disqualified count — what that action can actually surface —
	// not the unscoped total (which counts disqualified leads parked in other tabs, e.g. Ignored).
	// Fall back to the unscoped total when the caller doesn't provide the scoped count.
	const scopedDisqualified =
		scopedDisqualifiedCount === undefined ? disqualified : toCount(scopedDisqualifiedCount);

	if (draft + disqualified + engaged === 0) {
		return "never";
	}

	// There are still Found (qualified) leads somewhere — this view is empty for another reason
	// (a different tab or inclusion selection), so neither "filtered" nor "all clean" fits.
	if (draft > 0) {
		return null;
	}

	// Disqualified leads sit in THIS tab → offer "Review all" to surface them right here.
	if (scopedDisqualified > 0) {
		return "filtered";
	}

	// Leads exist on the list, but none are reviewable in this tab (all assigned or moved to
	// Ignored) → "aged", which offers "View ignored leads" to reach the disqualified ones.
	return "aged";
};
